import { useEffect } from "react"
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom"
import { AuthGuard } from "@/components/auth-guard"
import { ErrorBoundary } from "@/components/error-boundary"
import { AccountsPage } from "@/pages/accounts-page"
import { AuthPage } from "@/pages/auth-page"
import { DashboardPage } from "@/pages/dashboard-page"
import { EditorPage } from "@/pages/editor-page"
import { EmbedPage } from "@/pages/embed-page"
import { ChatbotTestPage } from "@/pages/chatbot-test-page"
import { NotFoundPage } from "@/pages/not-found-page"
import { InvitePage } from "@/pages/invite-page"
import { PasswordResetPage } from "@/pages/password-pages"

const EDITOR_SECTIONS = ["general", "appearance", "ai", "content", "analytics", "history", "users", "installation", "integrations"] as const

function isEditorSection(value: string | undefined): value is typeof EDITOR_SECTIONS[number] {
  return Boolean(value && EDITOR_SECTIONS.includes(value as typeof EDITOR_SECTIONS[number]))
}

function EditorSectionRedirect() {
  const { id = "" } = useParams()
  const [searchParams] = useSearchParams()
  const requested = searchParams.get("section") ?? undefined
  const section = isEditorSection(requested) ? requested : "general"
  return <Navigate to={`/chatbots/${id}/${section}`} replace />
}

function EditorRoute() {
  const { id = "", section } = useParams()
  if (!isEditorSection(section)) return <Navigate to={`/chatbots/${id}/general`} replace />
  return <ErrorBoundary><EditorPage /></ErrorBoundary>
}

export function App() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) window.requestAnimationFrame(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }))
    else window.scrollTo(0, 0)
  }, [hash, pathname])

  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/signup" element={<Navigate to="/login?mode=signup" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/login?mode=forgot" replace />} />
      <Route path="/password-reset" element={<PasswordResetPage />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/embed/:publicKey" element={<EmbedPage />} />
      <Route element={<AuthGuard />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/app" element={<Navigate to="/" replace />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/admin" element={<Navigate to="/accounts" replace />} />
        <Route path="/admin/accounts" element={<AccountsPage />} />
        {/* Cada seção do editor possui uma URL própria; o redirect mantém links legados com ?section=. */}
        <Route path="/chatbots/:id" element={<EditorSectionRedirect />} />
        <Route path="/chatbots/:id/test" element={<ChatbotTestPage />} />
        <Route path="/chatbots/:id/:section" element={<EditorRoute />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
